import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Twitter, Youtube } from 'lucide-react';
import { LeadCaptureForm } from '../forms/LeadCaptureForm';

const LOGO_URL = "/ninjathlete-wordmark.png";

const footerLinks = {
  explore: [
    { href: '/hyrox', label: 'Hyrox' },
    { href: '/hyrox/near-me', label: 'HYROX Near Me' },
    { href: '/locator', label: 'Recovery Locator' },
    { href: '/peptides', label: 'Peptides' },
    { href: '/peptide-subscription', label: 'Peptide Membership' },
    { href: '/blog', label: 'Blog' },
    { href: '/shop', label: 'Shop' },
    { href: '/gear-box', label: 'Gear Box' },
    { href: '/tools', label: 'Tools' },
    { href: '/tools/protein-calculator', label: 'Protein Calculator' },
    { href: '/about', label: 'About' },
  ],
  categories: [
    { href: '/blog?category=Meditation', label: 'Meditation' },
    { href: '/blog?category=Biohacking', label: 'Biohacking' },
    { href: '/blog?category=Nutrition', label: 'Nutrition' },
    { href: '/blog?category=Fitness', label: 'Fitness' },
  ],
  legal: [
    { href: '/privacy', label: 'Privacy Policy' },
    { href: '/terms', label: 'Terms of Service' },
    { href: '/contact', label: 'Contact' },
    { href: '/faq', label: 'FAQ' },
    { href: '/shipping', label: 'Shipping' },
    { href: '/returns', label: 'Start a Return' },
  ],
};

const socialLinks = [
  { href: 'https://instagram.com', icon: Instagram, label: 'Instagram' },
  { href: 'https://twitter.com', icon: Twitter, label: 'Twitter' },
  { href: 'https://youtube.com', icon: Youtube, label: 'YouTube' },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black border-t border-zinc-800" data-testid="main-footer">
      <div className="container-ninja section-spacing">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="NinjAthlete" 
                className="h-8 w-auto invert"
              />
            </Link>
            <p className="text-zinc-500 text-sm leading-relaxed mb-6">
              Optimize your performance. Master your mind. Unlock your potential through the ancient art of disciplined wellness.
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-zinc-800 text-zinc-500 hover:text-white hover:border-[#E63946] transition-colors duration-300"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Explore Links */}
          <div>
            <h4 
              className="text-sm uppercase tracking-widest text-zinc-400 mb-6"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Explore
            </h4>
            <ul className="space-y-3">
              {footerLinks.explore.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-zinc-500 hover:text-white transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories Links */}
          <div>
            <h4 
              className="text-sm uppercase tracking-widest text-zinc-400 mb-6"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Categories
            </h4>
            <ul className="space-y-3">
              {footerLinks.categories.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-zinc-500 hover:text-white transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 
              className="text-sm uppercase tracking-widest text-zinc-400 mb-6"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Join the Dojo
            </h4>
            <LeadCaptureForm 
              source="footer"
              variant="minimal"
              buttonText="Subscribe"
              interests={['newsletter']}
            />
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-zinc-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-600 text-sm">
            © {currentYear} NinjAthlete. All rights reserved.
            <span className="hidden sm:inline"> · </span>
            <a
              href="https://digitalmedianinja.com/services/web-design"
              target="_blank"
              rel="noopener"
              className="block sm:inline text-zinc-700 hover:text-zinc-400 transition-colors duration-300"
              data-testid="agency-credit-link"
            >
              Dallas Web Design by Digital Media Ninja
            </a>
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-zinc-600 hover:text-zinc-400 transition-colors duration-300 text-sm"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/admin"
              className="text-zinc-700 hover:text-zinc-400 transition-colors duration-300 text-sm"
              data-testid="admin-link"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
